import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';

// C2 · P6 — platform-admin moderation queue. Reads the pending-report queue via
// get_pending_reports (SECDEF, platform-admin-gated server-side) and acts on a
// flag via moderate_report. Admin has no i18n runtime — labels are plain English.

export interface PendingReport {
  id: string;
  report_type: string;
  description: string | null;
  created_at: string;
  reporter_name: string | null;
  reported_user_name: string | null;
  reported_message_id: string | null;
  reported_conversation_id: string | null;
  message_content: string | null;
  message_created_at: string | null;
  message_type: string | null;
}

export type ModerateAction = 'dismiss' | 'remove';

export function useModerationQueue() {
  const [reports, setReports] = useState<PendingReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc('get_pending_reports');
    if (!mountedRef.current) return;
    if (rpcError) {
      setError(rpcError.message);
    } else if (data?.success && Array.isArray(data.reports)) {
      setReports(data.reports as PendingReport[]);
    } else {
      setError(data?.error?.message ?? 'Failed to load reports.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- data-on-mount fetch in an awaited callback (admin standard pattern)
    void fetchReports();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchReports]);

  // Acts on a flag. On {success:true} the row is optimistically dropped from the
  // list; otherwise the error envelope's message is surfaced to the caller.
  const moderate = useCallback(
    async (reportId: string, action: ModerateAction): Promise<{ success: boolean; error?: string }> => {
      const { data, error: rpcError } = await supabase.rpc('moderate_report', {
        p_report_id: reportId,
        p_action: action,
      });
      if (rpcError) return { success: false, error: rpcError.message };
      if (data?.success) {
        if (mountedRef.current) setReports((prev) => prev.filter((r) => r.id !== reportId));
        return { success: true };
      }
      return { success: false, error: data?.error?.message ?? 'Action failed.' };
    },
    [],
  );

  return { reports, loading, error, refresh: fetchReports, moderate };
}
